import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import UserModal from "../components/UserModal";
import ConfirmModal from "../components/ConfirmModal";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, can, avText, avBg, avFg } from "../data/constants";
import { apiRequest } from "../services/api";
import { fetchAdminUsers, toggleUserStatus, updateUserDetails } from "../services/usersService";

// ── SVG assets from user folder ──────────────────────────────────────────────
import SearchIcon from "../assets/user/Search.svg";
import DropdownIcon from "../assets/user/dropdown.svg";
import FilterIcon from "../assets/user/filter.svg";
import EyeIcon from "../assets/user/eye.svg";
import ActionDotsIcon from "../assets/product/productlist/action_buttons.svg";
import EditIcon from "../assets/product/productlist/edit.svg";
import ApproveIcon from "../assets/product/productlist/approve.svg";
import RejectIcon from "../assets/product/productlist/reject.svg";

function StatusBadgeNew({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "approved") {
    return (
      <span className="bg-[#0B61721A] text-[#0B6172] border border-[#0B617233] px-3.5 py-1 rounded-full text-xs font-semibold">
        Active
      </span>
    );
  }
  return (
    <span className="bg-[#5E5E5E1A] text-gray-500 border border-[#5E5E5E33] px-3.5 py-1 rounded-full text-xs font-semibold">
      Inactive
    </span>
  );
}

function PaginationNew({ page, total, perPage, onChange }) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const getPages = () => {
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }
    if (page - delta > 2) {
      range.unshift("...");
    }
    if (page + delta < totalPages - 1) {
      range.push("...");
    }
    range.unshift(1);
    if (totalPages > 1) {
      range.push(totalPages);
    }
    return Array.from(new Set(range));
  };

  const pages = getPages();

  return (
    <div className="flex justify-between items-center px-[18px] py-[14px] border-t border-[#0000001A]">
      <p className="text-sm text-[#777]">
        Showing <strong className="text-[#333]">
          {Math.min((page - 1) * perPage + 1, total)}-
          {Math.min(page * perPage, total)}
        </strong>{" "}
        of <strong className="text-[#333]">{total}</strong> users
      </p>

      <div className="flex items-center gap-1.5">
        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          ‹
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="w-9 text-center text-[#888] text-sm">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm ${
                p === page
                  ? "bg-[#0B6172] text-white border-none font-bold"
                  : "border border-[#0000001A] bg-white text-[#444] font-medium cursor-pointer"
              }`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          )
        )}

        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === totalPages}
          onClick={() => onChange(Math.min(totalPages, page + 1))}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ── Three-dot action menu ────────────────────────────────────────────────────
function UserActionMenu({ userObj, currentUser, onView, onEdit, onToggleStatus }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const s = (userObj.status || "").toLowerCase();
  const isActive = s === "active" || s === "approved";
  const canToggle = can(currentUser.role, "del_u") && userObj.id !== currentUser.id;

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-lg border-none bg-transparent cursor-pointer flex items-center justify-center hover:bg-gray-100 transition-colors ml-auto"
      >
        <img src={ActionDotsIcon} alt="actions" className="w-[22px] h-[22px]" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 z-20 bg-white border border-[#0000001A] rounded-[10px] shadow-[0_0_20px_0_#0000001A] w-max overflow-hidden"
        >
            {[
              {
                label: "View Details",
                color: "text-gray-700",
                icon: EyeIcon,
                onClick: () => { setOpen(false); onView(); },
                show: true,
              },
              {
                label: "Edit",
                color: "text-gray-700",
                icon: EditIcon,
                onClick: () => { setOpen(false); onEdit(); },
                show: true,
              },
              {
                label: isActive ? "Inactive" : "Active",
                color: isActive ? "text-red-600" : "text-[#0B6172]",
                icon: isActive ? RejectIcon : ApproveIcon,
                onClick: () => { setOpen(false); onToggleStatus(); },
                show: canToggle,
              },
            ]
              .filter((item) => item.show)
              .map((item) => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] bg-transparent cursor-pointer hover:bg-gray-50 transition-colors border-t border-[#0000001A] first:border-t-0 ${item.color}`}
                >
                  <img src={item.icon} alt={item.label} className="w-4 h-4 shrink-0" />
                  {item.label}
                </button>
              ))}
          </div>
      )}
    </div>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const { setUsers, addToast } = useOutletContext();
  const [usersList, setUsersList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [pg, setPg] = useState(1);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortByDate, setSortByDate] = useState(false);
  const [modal, setModal] = useState({ open: false, editUser: null, viewOnly: false });
  const [confirmM, setConfirmM] = useState({ open: false });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const LIMIT = 10;

  const loadUsers = useCallback(async (page, role) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminUsers({
        page,
        limit: LIMIT,
        role: role || null,
      });
      const mapped = (data.items || []).map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role ? u.role.toLowerCase() : "",
        status: u.status ? u.status.toLowerCase() : "",
        createdDate: u.joined_date,
        phoneNumber: u.phoneNumber || u.phone_number || "",
      }));
      setUsersList(mapped);
      setTotal(data.total || 0);

      if (typeof setUsers === "function") {
        setUsers(new Array(data.total || 0).fill({}));
      }
    } catch (err) {
      setError(err.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, [setUsers]);

  useEffect(() => {
    loadUsers(pg, selectedRole);
  }, [pg, selectedRole, loadUsers]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setPg(1);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setPg(1);
  };

  const sortedAndFiltered = useMemo(() => {
    let result = usersList.filter((u) => {
      const matchesSearch = !search ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !selectedStatus ||
        (selectedStatus === "active" && (u.status === "active" || u.status === "approved")) ||
        (selectedStatus === "suspended" && (u.status === "suspended" || u.status === "inactive"));
      return matchesSearch && matchesStatus;
    });

    if (sortByDate) {
      result = [...result].sort((a, b) => {
        const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return dateB - dateA; // Newest first
      });
    }

    return result;
  }, [usersList, search, selectedStatus, sortByDate]);

  const handleSave = async (form) => {
    setModalLoading(true);
    setModalError(null);
    try {
      if (modal.editUser) {
        await updateUserDetails(modal.editUser.id, {
          name: form.name,
          phoneNumber: form.contactNumber || null,
        });
        addToast("User details updated successfully", "success");
      } else {
        await apiRequest("/admin/users/register", {
          method: "POST",
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            contactNumber: form.contactNumber,
            role: form.role,
          }),
        });
        addToast("User created ✓", "success");
      }
      setModal({ open: false, editUser: null, viewOnly: false });
      loadUsers(pg, selectedRole);
    } catch (err) {
      setModalError(err.message || "Failed to save user.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleStatus = async (u) => {
    setConfirmM({ open: false });
    try {
      const result = await toggleUserStatus(u.id);
      const newStatus = (result.status || "").toLowerCase();
      addToast(
        newStatus === "suspended"
          ? `"${u.name}" has been suspended.`
          : `"${u.name}" has been activated.`,
        newStatus === "suspended" ? "info" : "success"
      );
      loadUsers(pg, selectedRole);
    } catch (err) {
      addToast(err.message || "Failed to update user status.", "error");
    }
  };

  const handleBadgeClick = (u) => {
    if (!can(user.role, "del_u") || u.id === user.id) return;
    if (u.status === "suspended") {
      setConfirmM({
        open: true,
        title: "Activate User",
        message: `Re-enable login access for "${u.name}"?`,
        onConfirm: () => handleToggleStatus(u),
        variant: "success",
      });
    } else {
      setConfirmM({
        open: true,
        title: "Suspend User",
        message: `Suspend login access for "${u.name}"?`,
        onConfirm: () => handleToggleStatus(u),
        variant: "danger",
      });
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).toUpperCase();
  };

  const roleColorClass = (role) => {
    const r = (role || "").toLowerCase();
    if (r === "admin" || r === "super_admin") {
      return "bg-[#39B8AE] text-white";
    }
    return "bg-[#0B6172] text-white";
  };

  const roleLabelName = (role) => {
    const r = (role || "").toLowerCase();
    if (r === "admin" || r === "super_admin") {
      return "ADMIN";
    }
    if (r === "reviewer" || r === "moderator" || r === "editor") {
      return "REVIEWER";
    }
    return (ROLE_LABELS[r] || r).toUpperCase();
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Header card ──────────────────────────────────────────────────── */}
      <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] shadow-[0_4px_32px_0_#0000000A] backdrop-blur-[20px] px-6 py-5 flex flex-col min-[500px]:flex-row min-[500px]:items-center justify-between gap-4">
        <div>
          <h1 className="font-[800] text-2xl text-gray-900 m-0 font-['Google_Sans_Flex',sans-serif]">
            User Management
          </h1>
          <p className="text-[13px] text-[#0B6172] mt-1">
            Manage platform access for {total} team member{total !== 1 ? "s" : ""} in your organization.
          </p>
        </div>

        {can(user.role, "create_u") && (
          <button
            onClick={() => { setModalError(null); setModal({ open: true, editUser: null, viewOnly: false }); }}
            className="bg-[#0B6172] text-white border-none rounded-lg px-7 py-2.5 font-semibold text-sm cursor-pointer hover:bg-[#094f5e] transition-colors self-start min-[500px]:self-auto"
          >
            Add
          </button>
        )}
      </div>

      {/* ── Filters + table card ─────────────────────────────────────────── */}
      <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] overflow-hidden">
        {/* ── Top bar: search + dropdowns + filter ─────────────────────── */}
        <div className="flex items-center gap-3 px-[18px] py-4 flex-wrap">
          {/* Search */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 cursor-pointer text-sm text-[#444] flex-1 min-w-[220px]">
            <img src={SearchIcon} alt="search" className="w-4 h-4 shrink-0" />
            <input
              type="text"
              placeholder="Search by Name, Email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-none outline-none bg-transparent text-sm text-gray-600"
            />
          </div>

          {/* All Roles */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 relative min-w-[120px]">
            <select
              value={selectedRole}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="w-full bg-transparent border-none outline-none appearance-none pr-6 text-sm text-[#444] font-medium cursor-pointer"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="reviewer">Reviewer</option>
              <option value="agent">Agent</option>
            </select>
            <img src={DropdownIcon} alt="dropdown" className="w-3 h-2 absolute right-3 pointer-events-none" />
          </div>

          {/* All Statuses */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 relative min-w-[130px]">
            <select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full bg-transparent border-none outline-none appearance-none pr-6 text-sm text-[#444] font-medium cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Inactive</option>
            </select>
            <img src={DropdownIcon} alt="dropdown" className="w-3 h-2 absolute right-3 pointer-events-none" />
          </div>

          {/* Filter icon button */}
          {/* <div
            onClick={() => setSortByDate(prev => !prev)}
            className={`h-[42px] border rounded-lg flex items-center gap-2 px-3 cursor-pointer text-sm w-[42px] justify-center transition-all ${
              sortByDate
                ? "bg-[#0B61721A] border-[#0B6172] text-[#0B6172]"
                : "bg-white border-[#0000001A] text-[#444]"
            }`}
            title={sortByDate ? "Clear Date Sorting" : "Sort by Date"}
          >
            <img
              src={FilterIcon}
              alt="filter"
              className="w-[18px] h-3"
              style={sortByDate ? { filter: "invert(29%) sepia(21%) saturate(2203%) hue-rotate(145deg) brightness(91%) contrast(92%)" } : undefined}
            />
          </div> */}
        </div>

        {/* Horizontal scroll container for the table */}
        <div className="overflow-x-auto w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="min-w-[850px]">
            {/* ── Table header row ─────────────────────────────────────────── */}
            <div className="grid grid-cols-[4fr_2.5fr_2fr_2.5fr_1fr] bg-[#5E5E5E14] border border-b-0 border-[#0000001A] rounded-t-lg px-[18px] py-[13px] mx-[18px]">
              {["USER", "ROLE", "STATUS", "JOINED", "ACTIONS"].map((col, i) => (
                <div
                  key={col}
                  className={`text-xs font-bold text-gray-500 tracking-wider uppercase ${
                    i === 4 ? "text-right" : "text-left"
                  }`}
                >
                  {col}
                </div>
              ))}
            </div>

            {/* ── Table rows ───────────────────────────────────────────────── */}
            <div className="px-[18px] pt-2.5 pb-1.5 flex flex-col gap-2.5 overflow-y-auto max-h-[calc(100vh-450px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-7 h-7 border-2 border-[#DBF5F0] border-t-[#0B6172] rounded-full animate-spin" />
                  <p className="text-[13px] text-[#0B6172] font-semibold">Loading users...</p>
                </div>
              ) : sortedAndFiltered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
                  <p className="font-bold text-gray-500 text-[15px]">No users found</p>
                  <p className="text-[13px]">Try adjusting your search or filters</p>
                </div>
              ) : (
                sortedAndFiltered.map((u) => (
                  <div
                    key={u.id}
                    className="grid grid-cols-[4fr_2.5fr_2fr_2.5fr_1fr] items-center bg-white border border-[#0000001A] rounded-lg px-[18px] py-3.5"
                  >
                    {/* User info: avatar + name + email */}
                    <div className="flex items-center gap-3.5">
                      <div
                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{
                          background: avBg(u.name),
                          color: avFg(u.name),
                          border: `1.5px solid ${avFg(u.name)}40`,
                        }}
                      >
                        {avText(u.name)}
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-gray-900 m-0 leading-tight">
                          {u.name}
                        </p>
                        <p className="text-[12px] text-gray-400 mt-1">
                          {u.email}
                        </p>
                      </div>
                    </div>

                    {/* Role */}
                    <div>
                      <span
                        className={`text-[11px] font-bold px-5 py-1.5 rounded-full inline-block text-center min-w-[90px] ${roleColorClass(
                          u.role
                        )}`}
                      >
                        {roleLabelName(u.role)}
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <StatusBadgeNew status={u.status} />
                    </div>

                    {/* Joined */}
                    <div className="text-[13px] text-gray-500 font-medium uppercase">
                      {formatDate(u.createdDate)}
                    </div>

                    {/* Actions */}
                    <div className="text-right">
                      <UserActionMenu
                        userObj={u}
                        currentUser={user}
                        onView={() => { setModalError(null); setModal({ open: true, editUser: u, viewOnly: true }); }}
                        onEdit={() => { setModalError(null); setModal({ open: true, editUser: u, viewOnly: false }); }}
                        onToggleStatus={() => handleBadgeClick(u)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {total > LIMIT && (
          <PaginationNew page={pg} total={total} perPage={LIMIT} onChange={setPg} />
        )}
      </div>

      <UserModal
        open={modal.open}
        onClose={() => { setModal({ open: false, editUser: null, viewOnly: false }); setModalError(null); }}
        onSave={handleSave}
        editUser={modal.editUser}
        viewOnly={modal.viewOnly}
        isLoading={modalLoading}
        apiError={modalError}
      />
      <ConfirmModal {...confirmM} onClose={() => setConfirmM({ open: false })} />
    </div>
  );
}
